import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { StatCard } from '@/components/app/StatCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiClient, humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';
import { humanizePercent } from '@/lib/utils';

interface CatalogEntry {
  accessoryId: string;
  price: number;
  rarity: string;
  category: string;
  enabled: boolean;
  deprecated: boolean;
  canBuy: boolean;
  canEarn: boolean;
  gateLevel?: number;
  gateStreak?: number;
  sortOrder: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface PurchaseRecord {
  purchaseId: string;
  userId: string;
  accessoryId: string;
  priceAtPurchase: number;
  availableBalanceBefore: number;
  availableBalanceAfter: number;
  purchasedAt: string;
  status: 'completed' | 'failed';
  failureReason?: string;
}

interface StoreSummary {
  period: { startDate: string; endDate: string };
  totalEvents: number;
  byEventName: Record<string, number>;
  topAccessories: { accessoryId: string; count: number }[];
  purchaseSuccessRate: number;
}

interface StoreConfig {
  currency: {
    code: string;
    displayName: string;
    icon?: string;
    editable: { displayName: boolean; icon: boolean; code: boolean };
  };
  starterAccessoryAllowlist: { source: string; items: string[]; editable: boolean };
  goalSlotsByLevel: {
    source: string;
    tiers: { minLevel: number; maxGoals: number }[];
    editable: boolean;
  };
  pricingGuardrails?: { minXp?: number; maxXp?: number };
  meta: { updatedAt?: string; updatedBy?: string };
}

interface StoreConfigUpdate {
  currencyDisplayName?: string;
  currencyIcon?: string;
  pricingGuardrails?: { minXp?: number; maxXp?: number };
}

const keys = {
  catalog: () => ['store', 'catalog'] as const,
  purchases: (limit: number) => ['store', 'purchases', limit] as const,
  analytics: () => ['store', 'analytics'] as const,
  config: () => ['store', 'config'] as const,
};

const api = {
  catalog: () => apiClient.get<{ items: CatalogEntry[] }>('/store/catalog'),
  purchases: (limit: number) =>
    apiClient.get<{ purchases: PurchaseRecord[]; nextCursor?: string | null }>(
      '/store/purchases',
      { limit },
    ),
  analytics: () => apiClient.get<StoreSummary>('/store/analytics'),
  config: () => apiClient.get<StoreConfig>('/store/config'),
  updateConfig: (body: StoreConfigUpdate) =>
    apiClient.put<StoreConfig>('/store/config', body),
};

export function StoreHomePage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Store"
        description="Accessory catalog, purchase ledger, and event analytics."
      />

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog">Catalog</TabsTrigger>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
        </TabsList>
        <TabsContent value="catalog"><CatalogTab /></TabsContent>
        <TabsContent value="purchases"><PurchasesTab /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
        <TabsContent value="config"><ConfigTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function CatalogTab() {
  const q = useQuery({ queryKey: keys.catalog(), queryFn: () => api.catalog(), staleTime: 60_000 });
  const entries = React.useMemo(() => q.data?.items ?? [], [q.data?.items]);

  const grouped = React.useMemo(() => {
    const m = new Map<string, CatalogEntry[]>();
    for (const e of entries) {
      if (!m.has(e.category)) m.set(e.category, []);
      m.get(e.category)!.push(e);
    }
    return Array.from(m.entries()).map(([category, items]) => ({
      category,
      items: items.sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [entries]);

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Catalog failed" description={humanizeError(q.error)} />;
  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No catalog entries.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {grouped.map((g) => (
        <Card key={g.category}>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
              {g.category}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y text-sm">
              {g.items.map((e) => (
                <li key={e.accessoryId} className="flex items-center gap-3 px-4 py-2">
                  <code className="flex-1 font-mono text-xs">{e.accessoryId}</code>
                  <Badge variant="outline">{e.rarity}</Badge>
                  <span className="w-16 text-right">{e.price > 0 ? `${e.price} XP` : 'free'}</span>
                  {e.canBuy && <Badge variant="default">Buy</Badge>}
                  {e.canEarn && <Badge variant="success">Earn</Badge>}
                  {!e.enabled && <Badge variant="outline">disabled</Badge>}
                  {e.deprecated && <Badge variant="destructive">deprecated</Badge>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PurchasesTab() {
  const q = useQuery({ queryKey: keys.purchases(100), queryFn: () => api.purchases(100), staleTime: 30_000 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Purchases failed" description={humanizeError(q.error)} />;
  const purchases = q.data?.purchases ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        {purchases.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">No purchases yet.</p>
        ) : (
          <ul className="divide-y text-sm">
            {purchases.map((p) => (
              <li key={p.purchaseId} className="flex items-center justify-between px-4 py-2">
                <div>
                  <code className="text-xs">{p.accessoryId}</code>
                  <div className="text-xs text-muted-foreground">
                    {p.userId.slice(0, 8)}… · {p.priceAtPurchase} XP ·{' '}
                    {p.availableBalanceBefore} → {p.availableBalanceAfter} ·{' '}
                    {p.purchasedAt.slice(0, 10)}
                  </div>
                </div>
                <Badge variant={p.status === 'completed' ? 'success' : 'destructive'}>
                  {p.status}
                  {p.failureReason && ` · ${p.failureReason}`}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AnalyticsTab() {
  const q = useQuery({ queryKey: keys.analytics(), queryFn: () => api.analytics(), staleTime: 5 * 60_000 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data)
    return <ErrorState title="Analytics failed" description={humanizeError(q.error)} />;
  const s = q.data;
  return (
    <div className="space-y-3">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard
          label="Total events"
          value={s.totalEvents.toLocaleString()}
          hint={`${s.period.startDate.slice(0, 10)} → ${s.period.endDate.slice(0, 10)}`}
        />
        <StatCard label="Purchase success" value={humanizePercent(s.purchaseSuccessRate)} />
        <StatCard label="Unique accessories" value={Object.keys(s.byEventName).length} />
      </section>

      <Card>
        <CardHeader><CardTitle className="text-base">Events</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y text-sm">
            {Object.entries(s.byEventName)
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => (
                <li key={name} className="flex items-center justify-between px-4 py-2">
                  <code className="text-xs">{name}</code>
                  <span className="text-xs text-muted-foreground">{count}</span>
                </li>
              ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Top accessories</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y text-sm">
            {s.topAccessories.map((a) => (
              <li key={a.accessoryId} className="flex items-center justify-between px-4 py-2">
                <code className="text-xs">{a.accessoryId}</code>
                <span className="text-xs text-muted-foreground">{a.count}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function ConfigTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: keys.config(), queryFn: () => api.config(), staleTime: 60_000 });

  const [displayName, setDisplayName] = React.useState('');
  const [icon, setIcon] = React.useState('');
  const [minXp, setMinXp] = React.useState<string>('');
  const [maxXp, setMaxXp] = React.useState<string>('');
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!initialized && q.data) {
      setDisplayName(q.data.currency.displayName);
      setIcon(q.data.currency.icon ?? '');
      setMinXp(q.data.pricingGuardrails?.minXp?.toString() ?? '');
      setMaxXp(q.data.pricingGuardrails?.maxXp?.toString() ?? '');
      setInitialized(true);
    }
  }, [q.data, initialized]);

  const mutation = useMutation({
    mutationFn: (body: StoreConfigUpdate) => api.updateConfig(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.config() });
      toast({ title: 'Config updated', variant: 'success' });
    },
    onError: (err) =>
      toast({
        title: 'Update failed',
        description: humanizeError(err),
        variant: 'destructive',
      }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data)
    return <ErrorState title="Config failed" description={humanizeError(q.error)} />;

  const cfg = q.data;
  const currentMin = minXp === '' ? undefined : parseInt(minXp, 10);
  const currentMax = maxXp === '' ? undefined : parseInt(maxXp, 10);
  const guardrailsDirty =
    currentMin !== cfg.pricingGuardrails?.minXp || currentMax !== cfg.pricingGuardrails?.maxXp;
  const dirty =
    displayName !== cfg.currency.displayName ||
    icon !== (cfg.currency.icon ?? '') ||
    guardrailsDirty;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Currency</CardTitle>
          <CardDescription>
            Currency code is fixed (<code>{cfg.currency.code}</code>). Display name and icon are
            editable.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cur-code">Code (read-only)</Label>
            <Input id="cur-code" value={cfg.currency.code} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cur-name">Display name</Label>
            <Input
              id="cur-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="cur-icon">Icon (URL or emoji)</Label>
            <Input id="cur-icon" value={icon} onChange={(e) => setIcon(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pricing guardrails</CardTitle>
          <CardDescription>
            Advisory min/max price for catalog entries. Enforcement lives in code; these are hints
            for operators.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="min-xp">Min XP</Label>
            <Input
              id="min-xp"
              type="number"
              min={0}
              value={minXp}
              onChange={(e) => setMinXp(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max-xp">Max XP</Label>
            <Input
              id="max-xp"
              type="number"
              min={0}
              value={maxXp}
              onChange={(e) => setMaxXp(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Starter accessory allowlist</CardTitle>
          <CardDescription>
            Auto-granted on signup. Code-controlled: <code>{cfg.starterAccessoryAllowlist.source}</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cfg.starterAccessoryAllowlist.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {cfg.starterAccessoryAllowlist.items.map((id) => (
                <li key={id}>
                  <Badge variant="outline" className="font-mono text-xs">
                    {id}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Goal slots by level</CardTitle>
          <CardDescription>
            Code-controlled: <code>{cfg.goalSlotsByLevel.source}</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y text-sm">
            {cfg.goalSlotsByLevel.tiers.map((t) => (
              <li key={t.minLevel} className="flex items-center justify-between px-4 py-2">
                <span>
                  Level <span className="font-mono">≥ {t.minLevel}</span>
                </span>
                <span className="text-muted-foreground">
                  max <span className="font-medium text-foreground">{t.maxGoals}</span> goals
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-3 text-xs text-muted-foreground">
          <span>
            {cfg.meta.updatedAt
              ? `Last updated ${cfg.meta.updatedAt.slice(0, 19).replace('T', ' ')}${
                  cfg.meta.updatedBy ? ` by ${cfg.meta.updatedBy}` : ''
                }`
              : 'Not yet customized.'}
          </span>
          <Button
            disabled={!dirty || mutation.isPending}
            onClick={() => {
              const body: StoreConfigUpdate = {};
              if (displayName !== cfg.currency.displayName) body.currencyDisplayName = displayName;
              if (icon !== (cfg.currency.icon ?? '')) body.currencyIcon = icon;
              if (guardrailsDirty) {
                const guardrails: { minXp?: number; maxXp?: number } = {};
                if (currentMin !== undefined) guardrails.minXp = currentMin;
                if (currentMax !== undefined) guardrails.maxXp = currentMax;
                body.pricingGuardrails = guardrails;
              }
              mutation.mutate(body);
            }}
          >
            <Save className="mr-1 h-3.5 w-3.5" />
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
